import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  getPatientById,
  getClinicalFormsBulkPrint,
  logClinicalFormPrint,
} from "../../../../services/patientService";
import PrintShell from "../../../../components/UI/PrintShell";
import ClinicalFormPrintBlock from "./ClinicalFormPrintBlock";
import ClinicalFormAttachmentPrintBlock from "./ClinicalFormAttachmentPrintBlock";
import { loadPatientUiConfig } from "../../usePatientTabs";
import { mapWithConcurrency } from "../../../../utils/mapWithConcurrency";

const parseIds = (raw) =>
  String(raw || "")
    .split(",")
    .map((p) => Number(String(p).trim()))
    .filter((n) => Number.isInteger(n) && n > 0);

/**
 * Bulk print preview opened in a new tab from Eval & Orders → Action → Print.
 * Renders each selected form, then (optionally) its attachments, then the next
 * form. Print / Save as PDF uses the browser dialog via PrintShell.
 */
const PatientClinicalFormsBulkPrintPage = () => {
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const idsParam = searchParams.get("ids") || "";
  const includeAttachments =
    searchParams.get("attachments") === "1" ||
    searchParams.get("attachments") === "true";
  const formIds = useMemo(() => parseIds(idsParam), [idsParam]);

  const [patient, setPatient] = useState(null);
  const [items, setItems] = useState([]);
  const [missingFormIds, setMissingFormIds] = useState([]);
  const [error, setError] = useState("");
  const readyIdsRef = useRef(new Set());
  const [readyTick, setReadyTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!formIds.length) {
      setError("No forms selected for print");
      return undefined;
    }

    setError("");
    setItems([]);
    readyIdsRef.current = new Set();
    setReadyTick(0);

    (async () => {
      try {
        const uiConfig = await loadPatientUiConfig();
        if (cancelled) return;
        // Eval forms under Eval & Orders. Daily Logs now live in General Forms.
        const tabs = uiConfig.enabledTabKeys || [];
        if (!tabs.includes("eval") && !tabs.includes("documents")) {
          setError("Clinical forms are not enabled for this agency");
          return;
        }

        const [patientRes, bulkRes] = await Promise.all([
          getPatientById(patientId),
          getClinicalFormsBulkPrint(patientId, {
            ids: formIds,
            includeAttachments,
          }),
        ]);
        if (cancelled) return;
        const patientData = patientRes.result || patientRes.data || patientRes;
        setPatient(patientData.patient || patientData);
        const payload = bulkRes.result || bulkRes.data || bulkRes;
        setItems(payload.items || []);
        setMissingFormIds(payload.missingFormIds || []);
        if (!(payload.items || []).length) {
          setError("None of the selected forms could be loaded");
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load print preview");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patientId, formIds, includeAttachments]);

  const markFormReady = useCallback((formId) => {
    if (formId == null || readyIdsRef.current.has(formId)) return;
    readyIdsRef.current.add(formId);
    setReadyTick((n) => n + 1);
  }, []);

  const allFormsReady =
    items.length > 0 &&
    readyTick >= 0 &&
    items.every((item) => readyIdsRef.current.has(item.form?.id)) &&
    Boolean(patient);

  const handlePrinted = useCallback(() => {
    const ids = items
      .map((item) => item?.form?.id)
      .filter((id) => id != null);
    // Avoid bursting N print-log POSTs at once after bulk print.
    void mapWithConcurrency(ids, 3, (id) =>
      logClinicalFormPrint(patientId, id).catch(() => null),
    );
  }, [items, patientId]);

  const patientName = patient
    ? [patient.firstName, patient.middleName, patient.lastName]
        .filter(Boolean)
        .join(" ") || patient.patientName
    : "";

  if (error && !items.length) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 px-6">
        <div className="max-w-sm rounded-xl border border-slate-200 bg-white px-6 py-5 text-center shadow-sm">
          <p className="text-sm font-semibold text-slate-800">
            Print preview could not be loaded
          </p>
          <p className="mt-1 text-xs text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!patient || !items.length) {
    return (
      <div className="min-h-screen animate-pulse bg-slate-100 py-10">
        <div className="mx-auto h-[700px] w-full max-w-4xl rounded-xl border border-slate-200 bg-white" />
      </div>
    );
  }

  return (
    <PrintShell
      title={`Print ${items.length} form${items.length === 1 ? "" : "s"}`}
      subtitle={`${patientName || "Patient"} · MRN ${patient.mrn || "—"}${
        includeAttachments ? " · with attachments" : ""
      }`}
      ready={allFormsReady}
      onPrinted={handlePrinted}
    >
      {missingFormIds.length ? (
        <div className="no-print mx-auto mb-3 w-full max-w-4xl rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          {missingFormIds.length} selected form
          {missingFormIds.length === 1 ? " was" : "s were"} not found and
          skipped.
        </div>
      ) : null}

      <div className="space-y-6 print:space-y-0">
        {items.map((item, index) => {
          const form = item.form;
          const attachments = item.attachments || [];
          return (
            <div
              key={form.id}
              className={`print-form-group ${
                index < items.length - 1 ? "print-sheet-break" : ""
              }`}
            >
              <ClinicalFormPrintBlock
                patient={patient}
                clinicalForm={form}
                onReady={() => markFormReady(form.id)}
              />
              {attachments.map((att) => (
                <div key={att.id} className="mt-6 print-attachment-break">
                  <ClinicalFormAttachmentPrintBlock
                    attachment={att}
                    formName={form.formName}
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </PrintShell>
  );
};

export default PatientClinicalFormsBulkPrintPage;
